import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { useConfig } from '../../hooks';
import IconButton from '../IconButton';
import DislikeIcon from '../Icons/Dislike';
import LikeIcon from '../Icons/Like';

const Feedback = ({ onLike, onDislike, feedback, horizontalPosition, isLiked, isDisliked }) => {
  const { message } = useConfig();
  const { i18n, t } = useTranslation();

  /**
   * `true` if the user has already
   * liked or dislike the message.
   */
  const [isSent, setSent] = useState(false);

  const handleLike = () => {
    if (!onLike) {
      return;
    }

    onLike();
    setSent(true);
  };

  const handleDislike = () => {
    if (!onDislike) {
      return;
    }

    onDislike();
    setSent(true);
  };

  if (!feedback) return null;

  return (
    <GroupBtn horizontalPosition={horizontalPosition} isSentFeedback={isSent}>
      <ButtonIcon
        color={message.likeColor}
        onClick={handleLike}
        isInactive={!isLiked && isSent}
        aria-label="like"
        title={i18n.exists('altText.positiveFeedback') ? t('altText.positiveFeedback') : ''}
      >
        <LikeIcon fill={message.likeColor} />
      </ButtonIcon>
      <ButtonIcon
        color={message.dislikeColor}
        onClick={handleDislike}
        isInactive={!isDisliked && isSent}
        aria-label="dislike"
        title={i18n.exists('altText.negativeFeedback') ? t('altText.negativeFeedback') : ''}
      >
        <DislikeIcon fill={message.dislikeColor} />
      </ButtonIcon>
    </GroupBtn>
  );
};

Feedback.propTypes = {
  /**
   * If `true` a component will be shown which
   * is intended for user feedback.
   */
  feedback: PropTypes.bool,
  /**
   * Function fired when user clicks on `Like` button.
   */
  onLike: PropTypes.func,
  /**
   * Function fired when user clicks on `Dislike` button.
   */
  onDislike: PropTypes.func,
  /**
   * If `true` a component will be shown with an active 'like' icon
   * and inactive 'dislike' icon after sending a feedback.
   */
  isLiked: PropTypes.bool,
  /**
   * If `true` a component will be shown with an active 'dislike' icon
   * and inactive 'like' icon after sending a feedback.
   */
  isDisliked: PropTypes.bool,
  /**
   * If `true` a component will be shown horizontal.
   */
  horizontalPosition: PropTypes.bool
};

const ButtonIcon = styled(IconButton)`
  width: 22px;
  height: 22px;
  background-color: #fff;
  border-radius: 100%;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  &:nth-of-type(n + 2) {
    margin-top: 8px;
  }
  &:hover {
    box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.2);
  }
  svg {
    width: 22px;
    height: 22px;
  }
  ${({ isInactive }) => {
    if (isInactive) {
      return css`
        filter: grayscale(1);
        opacity: 0.5;
      `;
    }
    return null;
  }}

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    padding: 1px;
  }
`;

const GroupBtn = styled.div`
  position: absolute;
  left: 100%;
  bottom: 0px;
  padding-left: 8px;
  pointer-events: ${props => (props.isSentFeedback ? 'none' : 'auto')};
  ${({ horizontalPosition }) => {
    if (horizontalPosition) {
      return css`
        display: flex;
        align-items: center;
        ${ButtonIcon}:nth-of-type(n + 2) {
          margin-left: 8px;
          margin-top: 0;
        }
      `;
    }
    return null;
  }}
`;

export default Feedback;
